import { Component, Input, ViewEncapsulation, ViewChild, OnInit } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { BaseModalComponent } from '../../../models/base-modal-component';
import * as _ from 'underscore';
import { DrObject } from '../../../models/dr-object';
import { Endpoint, HelpIndex, OperationType } from '../../../models/enums';
import { Role } from '../../../models/role';
import { User, UserViewModel } from '../../../models/user';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { ChangePasswordModalComponent } from '../change-password-modal/change-password-modal.component';
import { KeyValueListComponent } from '../../key-value-list/key-value-list.component';
import { cloneDeep } from 'lodash';
import { DrNamedQuery } from '../../../models/dr-named-query';
import { NgForm } from '@angular/forms';
import { PasswordAndUserLogoutPolicy } from '../../../models/password-user-logout-policy';
import { isNullOrUndefined } from '../../../models/utils';

@Component({
    standalone: false,
    selector: 'res-new-user-modal',
    templateUrl: './new-user-modal.component.html',
    styleUrls: ['./new-user-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class NewUserModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild('errorPopover', { static: true }) errorPopover: PopoverDirective;
    @ViewChild(KeyValueListComponent, { static: true }) keyValueList: KeyValueListComponent;
    @ViewChild('contentForm') contentForm: NgForm;

    validateNameRequest = {
        endpoint: Endpoint.viewManager_validateName,
        request: (nameToValidate) => {
            return {
                name: nameToValidate ? nameToValidate : this.nameDescriptionComponent.name,
                organizationName: this.commonService.currentUser.originalCustomerName === 'super_system_customer' && this.isSystemUser ? 'super_system_customer' : this.commonService.currentUser.customerName,
                objectType: this.nameDescriptionComponent.drObject.objectType,
                systemScope: this.isSystemUser
            };
        }
    };

    modalTitle = 'New Organization User';
    primaryButtonLabel = 'OK';
    secondaryButtonLabel = 'Cancel';
    isBusy = false;
    isBusyMessage = 'Loading...';
    modalClass = 'new-user-modal';
    modalAutomationId = 'new-edit-user-modal';
    modalHelpIndex = HelpIndex.add_edit_user_dlg;

    @Input() selectedDomain: string;
    @Input() selectedRole: Role;
    @Input() userName: string;
    @Input() password: string;
    @Input() firstName: string;
    @Input() lastName: string;
    @Input() email: string;
    @Input() description: string;
    @Input() authenticationPassword = true;
    @Input() mfa = false;
    @Input() conditionalOnIPAddress = null;
    @Input() authorizeIPAddresses = false;
    @Input() originalAuthorizeIPAddresses = false;

    ipAddressesList = [];
    originalIPAddressesList = [];
    domainList: Array<any>;
    selectDomain: any;
    roleList: Array<Role>;
    allMembers: Array<any>;
    createUser: User;
    endpoint: Endpoint;
    isEditUser = false;
    request: any;
    isSystemUser: boolean;

    isDescriptionHidden = true;
    drObject: UserViewModel;
    showPopover: boolean;
    tooltip: string;
    disableAllOptions: boolean;
    userHasPasswordChanged = false;
    gridUserList: Array<User>;
    roleHandle: string;
    enablePasswordFieldForTPAuth = true;
    lastCheckedEmail: string;
    passwordPolicy: PasswordAndUserLogoutPolicy;
    isRetryTpAuth = false;
    emailErrorPopoverMessage = 'Email format error/s';
    constructor(
        private modalService: BsModalService,
        private changePasswordModalRef: BsModalRef,
        private restService: RestService,
        protected commonService: CommonService) {
        super(modalService, changePasswordModalRef);
    }

    ngOnInit(): void {
        this.nameDescriptionComponent.drObject = this.drObject = new UserViewModel(new User());
        this.isSystemUser = this.modalData.isSystemUser;
        this.gridUserList = this.modalData.gridUserList;
        this.getMailConfiguration();
        if (this.isSystemUser) {
            this.modalHelpIndex = HelpIndex.add_edit_system_user_dlg;
            this.loadSystemRoles();
        } else {
            this.loadRoles();
        }
        this.getDomains();
        this.isEditUser = <OperationType>this.modalData.operationType === OperationType.edit;
        this.modalTitle = this.isEditUser ? (this.isSystemUser ? 'Edit System User' : 'Edit Organization User') : (this.isSystemUser ? 'New System User' : 'New Organization User');
        this.enablePasswordFieldForTPAuth = !this.commonService.isTPAuthEnabledSystem;
        if (this.isEditUser) {
            this.userName = this.nameDescriptionComponent.name = this.drObject.original.userName = this.modalData.userItem.userName;
            this.selectedDomain = this.drObject.original.domainHandle = this.modalData.userItem.domainHandle;
            this.password = this.modalData.userItem.password;
            this.firstName = this.drObject.original.firstName = this.modalData.userItem.firstName;
            this.lastName = this.drObject.original.lastName = this.modalData.userItem.lastName;
            this.email = this.drObject.original.email = this.modalData.userItem.email;
            this.description = this.drObject.original.description = this.modalData.userItem.description === null ? '' : this.modalData.userItem.description;
            this.drObject.original.userRole = this.modalData.userItem.userRole;
            this.mfa = this.drObject.original.mfa = this.modalData.userItem.mfa;
            const ipAddressRange = this.drObject.original.allowedIPAddressRange = this.modalData.userItem.allowedIPAddressRange;
            this.conditionalOnIPAddress = this.drObject.original.conditionalOnIPAddress = this.modalData.userItem.conditionalOnIPAddress;
            this.isRetryTpAuth = this.modalData?.isRetryTPAuth;
            if (this.isRetryTpAuth) {
                this.getPasswordPolicy();
            }
            if (!this.mfa) {
                this.conditionalOnIPAddress = null;
                this.drObject.original.conditionalOnIPAddress = null;
            }
            if (ipAddressRange && ipAddressRange.length > 0) {
                this.authorizeIPAddresses = true;
                this.originalAuthorizeIPAddresses = this.authorizeIPAddresses;
                ipAddressRange.forEach((row) => {
                    this.ipAddressesList.push(new DrNamedQuery({ name: row }));
                });
            }
            this.disableAllOptions = this.modalData.userItem.importedFromSystemUser;
            if (this.disableAllOptions) {
                this.tooltip = 'Authentication Method options cannot be configured for System Users.';
            }
        }
        if (!this.isEditUser) {
            this.getPasswordPolicy();
        }
        this.ipAddressesList.push({ name: "", query: "", description: null, annotationText: null });
        this.originalIPAddressesList = cloneDeep(this.ipAddressesList);

        this.drObject.setInitialized(true);
    }

    getPasswordPolicy(): void {
        this.restService.post(this.commonService?.isTPAuthEnabledSystem ? Endpoint.realmManager_getTPAuthPasswordPolicy : Endpoint.realmManager_getPasswordPolicy, { systemScope: true }).then(policyResponse => {
            if (policyResponse) {
                if (this.commonService?.isTPAuthEnabledSystem) {
                    this.passwordPolicy = new PasswordAndUserLogoutPolicy({
                        minimumPasswordLength: policyResponse?.tpAuthPasswordPolicy?.requirements?.minLength ? policyResponse?.tpAuthPasswordPolicy.requirements.minLength : 0,
                    });
                } else {
                    this.passwordPolicy = new PasswordAndUserLogoutPolicy({
                        minimumPasswordLength: policyResponse?.minimumPasswordLength ? policyResponse?.minimumPasswordLength : 0,
                        minimumUppercaseLetters: policyResponse?.minimumUppercaseLetters ? policyResponse?.minimumUppercaseLetters : 0,
                        minimumLowercaseLetters: policyResponse?.minimumLowercaseLetters ? policyResponse?.minimumLowercaseLetters : 0,
                        minimumNumbers: policyResponse?.minimumNumbers ? policyResponse?.minimumNumbers : 0,
                        minimumSymbols: policyResponse?.minimumSymbols ? policyResponse?.minimumSymbols : 0
                    });
                }
            }
        }, error => {
            this.isBusy = false;
        });
    }

    ngAfterViewInit(): void {
        this.onChangeAuthorizedIP();
    }

    getMailConfiguration(): void {
        this.restService.post(Endpoint.realmManager_getMailServerConfig, { systemScope: this.isSystemUser }).then(response => {
            if (response && response.mailServerConfig) {
                this.commonService.mailServerConfig = response.mailServerConfig;
            } else {
                this.tooltip = 'An Email Server has not been configured for this system.';
                this.mfa = false;
                this.conditionalOnIPAddress = null;
            }
        });
    }

    onPasswordChanged(): void {
        this.password = this.contentForm?.value?.password;
        if (this.passwordPolicy) {
            this.passwordPolicy.resetPasswordPolicyRequirements();
        }
    }

    onPasswordInputFocusedOutEvent(): void {
        this.password = this.password?.trim();
        if (this.password) {
            this.isBusy = true;
            this.isBusyMessage = 'Checking password ...';
            if (this.commonService?.isTPAuthEnabledSystem) {
                this.restService.post(Endpoint.userManager_validatePasswordForTPAuthPolicy, { password: this.password }).then((policyResponse) => {
                    if (policyResponse?.errors) {
                        this.passwordPolicy.minimumPasswordLengthMet = false;
                        this.passwordPolicy.showInvalid = true;
                    } else {
                        this.passwordPolicy.minimumPasswordLengthMet = true;
                    }
                    this.isBusy = false;
                }, () => {
                    this.isBusy = false;
                });
            } else {
                this.passwordPolicy.checkRequirements(this.contentForm?.value?.password);
                this.passwordPolicy.showInvalid = true;
                this.isBusy = false;
            }
        }
    }

    onEmailChanged(): void {
        if (this.commonService.isTPAuthEnabledSystem && this.enablePasswordFieldForTPAuth && !this.email) {
            this.onEmailBlur();
            this.enablePasswordFieldForTPAuth = false;
        }
    }

    getDomains(): void {
        this.domainList = [];
        this.restService.post(Endpoint.orgManager_listLdapDomains, { systemScope: this.isSystemUser }).then(response => {
            if (response && response.ldapDomains) {
                response.ldapDomains.forEach(entity => {
                    this.domainList.push(entity);
                });
                if (this.domainList.length > 0) {
                    if (this.commonService.currentUser.domainHandle) {
                        const userDomain = _.indexOf(this.domainList, (item) => item.domainList === item.currentUser.domainHandle);
                        this.selectDomain = this.domainList[userDomain];
                    } else {
                        const userDomain = _.indexOf(this.domainList, (item) => item.domainList === 'local');
                        this.selectDomain = this.domainList[userDomain];
                    }
                    if (!this.selectDomain) {
                        this.selectDomain = this.domainList[0];
                    }
                }
            }
        }, () => {
            this.isBusy = false;
        });

    }

    loadSystemRoles(): void {
        this.isBusy = true;
        this.restService.post(Endpoint.realmManager_listSystemRoles, { objectType: 'ALL', systemScope: this.isSystemUser }).then(responseListRoles => {
            if (responseListRoles && responseListRoles.roles) {
                this.roleList = responseListRoles.roles.map(role => new Role(role));
            }

            let index = 0;
            if (this.isEditUser && this.modalData.userItem && this.modalData.userItem.roleHandles) {
                index = this.roleList.findIndex(r => r.handle === this.modalData.userItem.roleHandles[0]);
            } else {
                index = this.roleList.findIndex(r => r.isDefault());
            }
            if (index < 0) {
                index = 0;
            }
            this.selectedRole = this.roleList[index];
            if (this.drObject.isInitialized() === false) {
                this.drObject.original.userRole = this.selectedRole.name;
            }
            this.roleHandle = this.selectedRole.handle;
            this.isBusy = false;
        }, error => {
            this.isBusy = false;
        });
    }

    loadRoles(): void {
        this.isBusy = true;
        this.restService.post(Endpoint.orgManager_listRoles, { objectType: 'ALL', systemScope: this.isSystemUser }).then(responseListRoles => {
            if (responseListRoles && responseListRoles.roles) {
                this.roleList = responseListRoles.roles.map(role => new Role(role));
            }

            let index = 0;
            if (this.isEditUser && this.modalData.userItem && this.modalData.userItem.roleHandles) {
                index = this.roleList.findIndex(r => r.handle === this.modalData.userItem.roleHandles[0]);
            } else {
                index = this.roleList.findIndex(r => r.isDefault());
            }
            if (index < 0) {
                index = 0;
            }
            this.selectedRole = this.roleList[index];
            if (this.drObject.isInitialized() === false) {
                this.drObject.original.userRole = this.selectedRole.name;
            }
            this.roleHandle = this.selectedRole.handle;
            this.isBusy = false;
        }, error => {
            this.isBusy = false;
        });
    }

    changeRole() {
        const index = this.roleList.findIndex(r => r.handle === this.roleHandle);
        if (index >= 0) {
            this.selectedRole = this.roleList[index];
        }
    }
    parseMember(entity: any): any {
        if (entity) {
            if (this.roleList) {
                entity.roleList = this.getUserRole(entity.roleHandles);
            }
        }
    }

    getUserRole(roleHandles: Array<string>): any {
        if (roleHandles && roleHandles.length > 0) {
            return this.roleList.filter(r => r.handle === roleHandles[0]).pop();
        }
        return null;
    }

    changePassword(): void {
        this.commonService.openModal(ChangePasswordModalComponent, {
            backdrop: false, class: 'horizontal-px-500',
            modalData: { entityType: 'change-password-no-current',
                userName: this.modalData.userItem.userName,
                isSystemUser: this.isSystemUser }
        }).then(result => {
            if (result && result.modalResult) {
                this.userHasPasswordChanged = true;
            }
        });
    }

    validate(): void {
        this.nameDescriptionComponent.validate().then(valid => {
            if (valid) {
                this.createNewUser();
            }
        });
    }

    onEmailBlur(): void {
        if (this.commonService.isTPAuthEnabledSystem) {
            if (this.email && this.email !== this.lastCheckedEmail) {
                this.isBusy = true;
                this.isBusyMessage = 'Checking user ...';
                this.contentForm.value.password = '';
                this.onPasswordChanged();
                this.restService.post(this.validateNameRequest.endpoint, this.nameDescriptionComponent.validateNameRequest.request(this.email)).then(response => {
                    if (response?.valid) {
                        this.restService.post(Endpoint.userManager_isUserInTPAuth, { userName: this.email }).then((response) => {
                            if (response) {
                                this.enablePasswordFieldForTPAuth = !response.userInTPAuth;
                                if (this.enablePasswordFieldForTPAuth) {
                                    this.firstName = '';
                                    this.lastName = '';
                                } else {
                                    this.restService.post(Endpoint.userManager_getTPAuthUserInfo, { email: this.email }).then((userInforesponse) => {
                                        const tpAuthUserInfo = userInforesponse?.tpAuthUserInfo;
                                        if (tpAuthUserInfo) {
                                            this.firstName = tpAuthUserInfo.firstName;
                                            this.lastName = tpAuthUserInfo.lastName;
                                        }
                                    })
                                }
                            }
                            this.isBusy = false;
                            this.lastCheckedEmail = this.email;
                        }, () => {
                            this.isBusy = false;
                            this.enablePasswordFieldForTPAuth = false;
                        });
                    } else {
                        this.isBusy = false;
                        this.enablePasswordFieldForTPAuth = false;
                        this.emailErrorPopoverMessage = response.reason;
                        this.showPopover = true;
                    }
                });
            } else {
                this.contentForm.value.password = '';
                this.onPasswordChanged();
                this.lastCheckedEmail = '';
            }
        }
    }

    createNewUser(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Creating user ...';
        const ipList = this.ipAddressesList.filter((ip) => ip.name != '').map(a => a.name);
        const allowedIPAddressRange = this.authorizeIPAddresses ? ipList : null;
        this.endpoint = this.isSystemUser ? Endpoint.adminOrgManager_createUser : Endpoint.orgManager_createUser;
        this.request = {
            domainHandle: this.selectDomain.name,
            local: true,
            roleHandles: [this.selectedRole.handle],
            password: this.enablePasswordFieldForTPAuth ? this.password : null,
            userName: this.commonService.isTPAuthEnabledSystem ? this.email : this.nameDescriptionComponent.name,
            email: this.email?.trim(),
            description: this.description,
            firstName: this.firstName,
            lastName: this.lastName,
            mfa: this.mfa,
            conditionalOnIPAddress: this.conditionalOnIPAddress === null ? false : this.conditionalOnIPAddress,
            allowedIPAddressRange: allowedIPAddressRange,
            systemScope: this.isSystemUser
        };

        if (this.isSystemUser) {
            this.request.orgName = 'super_system_customer';
        }
        this.createUpdateUser();
    }

    ok(): void {
        if (!this.isEditUser) {
            if (this.commonService.isTPAuthEnabledSystem) {
                this.createNewUser();
            } else {
                this.validate();
            }
        } else {
            if (this.isRetryTpAuth) {
                this.closeModal(true, { password: this.password });
            } else {
                const ipList = this.ipAddressesList.filter((ip) => ip.name != '').map(a => a.name);
                const allowedIPAddressRange = this.authorizeIPAddresses ? ipList : null;
                this.isBusyMessage = 'Updating user ...';
                this.endpoint = Endpoint.userManager_updateUser;
                this.request = {
                    description: this.description,
                    email: this.email,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    userHandle: this.modalData.userItem.handle,
                    roleHandles: [this.selectedRole.handle],
                    mfa: this.mfa,
                    conditionalOnIPAddress: this.conditionalOnIPAddress === null ? false : this.conditionalOnIPAddress,
                    allowedIPAddressRange: allowedIPAddressRange,
                    systemScope: this.isSystemUser
                };
                this.createUpdateUser();
            }
        }
    }

    createUpdateUser(): void {
        if (this.endpoint && this.request) {
            this.isBusy = true;
            this.restService.post(this.endpoint, this.request, false).then(response => {
                this.isBusy = false;
                if (response) {
                    if (this.commonService.currentUser.loginUsername === response.user?.handle) {
                        this.commonService.setCacheData('mail', response.user.email);
                    }
                    this.closeModal(true, response.user);
                }
            }, error => {
                this.isBusy = false;
                if (error.invalidIpAddresses) {
                    this.ipAddressesList.forEach((ip, index) => error.invalidIpAddresses.forEach((serverIP) => {
                        if (ip.name === serverIP && ip.name !== '') {
                            this.keyValueList.displayError(index, 'IP address format error/s ');
                            ip.hasError = true;
                        }
                    }));
                }
                switch (error.extendedStatus) {
                    case 'Valid email address is needed': {
                        this.emailErrorPopoverMessage = error.extendedStatus;
                        this.showPopover = true;
                        break;
                    }
                    case 'An Email Server has not been configured for this system.': {
                        this.tooltip = error.extendedStatus;
                        this.mfa = false;
                        this.conditionalOnIPAddress = null;
                        break;
                    }
                    default: {
                        if (error.extendedStatus !== 'Invalid IP Addresses') {
                            this.commonService.openErrorModal(error.extendedStatus);
                        }
                    }
                }
            });
        }
    }

    hasValidIPOptionsState(): boolean {
        if (this.authorizeIPAddresses && (this.ipAddressesList === undefined || this.ipAddressesList[0]?.name?.length === 0)) {
            return false;
        }
        return (!this.mfa || !this.conditionalOnIPAddress) || (this.authorizeIPAddresses && this.ipAddressesList[0]?.name?.length > 0);
    }

    isPrimaryButtonEnabled(): boolean {
        const emailFirstLastNameNotEmpty = !DrObject.isEmpty(this.firstName) && !DrObject.isEmpty(this.lastName) && !DrObject.isEmpty(this.email);

        if (this.isEditUser) {
            return this.isDirty() && this.hasValidIPOptionsState() && emailFirstLastNameNotEmpty;
        } else {
            let enabled = false;
            if (!this.isBusy && !this.showPopover) {
                if (this.commonService.isTPAuthEnabledSystem) {
                    enabled = emailFirstLastNameNotEmpty && (this.enablePasswordFieldForTPAuth ? !DrObject.isEmpty(this.password) && this.passwordPolicy?.minimumPasswordLengthMet : true);
                } else {
                    if (!DrObject.isEmpty(this.nameDescriptionComponent.name) && !DrObject.isEmpty(this.password) && emailFirstLastNameNotEmpty && this.passwordPolicy?.isPasswordPolicyRequirementsForStandardAuthPassed()) {
                        if (this.nameDescriptionComponent.isNameDirty() && this.nameDescriptionComponent.formValid) {
                            enabled = true;
                        }
                    }
                }
            }
            return enabled && this.hasValidIPOptionsState();
        }
    }

    isDirty(): boolean {
        if (this.email === '' && this.mfa) {
            return false;
        }
        if (((DrObject.isValueDirty(this.userName, this.drObject.original.userName)) || (DrObject.isValueDirty(this.firstName, this.drObject.original.firstName)) ||
            (DrObject.isValueDirty(this.lastName, this.drObject.original.lastName)) || (this.selectedRole && DrObject.isValueDirty(this.selectedRole.name, this.drObject.original.userRole)) ||
            (DrObject.isValueDirty(this.description, this.drObject.original.description)) || (DrObject.isValueDirty(this.email, this.drObject.original.email) ||
                this.mfa !== this.drObject.original.mfa || this.conditionalOnIPAddress !== this.drObject.original.conditionalOnIPAddress ||
                (this.authorizeIPAddresses === this.originalAuthorizeIPAddresses && !_.isEqual(this.ipAddressesList, this.originalIPAddressesList)) ||
                this.authorizeIPAddresses !== this.originalAuthorizeIPAddresses
            ))) {
            return true;
        }

        if (this.commonService.isTPAuthEnabledSystem && this.isRetryTpAuth && this.passwordPolicy?.minimumPasswordLengthMet) {
            return !DrObject.isEmpty(this.password);
        }
        return false;
    }

    onChangeAuthorizedIP() {
        this.keyValueList.showClearBtnAlways = true;
        if (!this.authorizeIPAddresses) {
            this.keyValueList.isDisabled = this.authorizeIPAddresses;
            this.ipAddressesList = [{ name: "", query: "", description: null, annotationText: null }];
            setTimeout(() => {
                this.keyValueList.isDisabled = !this.authorizeIPAddresses;
            });
        } else {
            this.keyValueList.isDisabled = !this.authorizeIPAddresses;
            this.keyValueList.ngOnInit();
            this.keyValueList.resetState();
        }
    }

    onChangeSecurityCode() {
        if (this.mfa && this.conditionalOnIPAddress === null) {
            this.conditionalOnIPAddress = false;
        }
        if (!this.mfa) {
            this.conditionalOnIPAddress = null;
        }
        this.showPopover = false;
    }

    onFocus(): void {
        this.showPopover = false;
    }

    hideRoleOption(role) {
        if (this.isSystemUser) {
            const sysAdminRole: any = this.roleList.find(role => role.name === 'System Administrator');
            const sysITAdminRole: any = this.roleList.find(role => role.name === 'IT Administrator');
            let userHasSysRole: any = this.commonService.currentUser.roles.find(role => role.name === 'System Administrator');
            let userHasITRole: any = this.commonService.currentUser.roles.find(role => role.name === 'IT Administrator');
            if (this.commonService.currentUser.systemRoles && !userHasSysRole) {
                userHasSysRole = this.commonService.currentUser.systemRoles.find(role => role.name === 'System Administrator');
            }
            const systemAdminsList = this.gridUserList.filter(role => role.userRole === 'System Administrator');

            if (this.isEditUser && systemAdminsList.length === 1 && this.modalData?.userItem?.roleHandles[0] === sysAdminRole.handle) {
                return role.handle === sysAdminRole.handle ? false : true;
            }

            if (!userHasITRole && this.isEditUser && this.modalData?.userItem?.roleHandles[0] === sysITAdminRole.handle) {
                return role.handle === sysITAdminRole.handle ? false : true;
            }

            if (userHasITRole && !this.isEditUser && (role.handle === sysITAdminRole.handle || role.handle === sysAdminRole.handle)) {
                return false;
            }

            if (userHasSysRole && role.handle === sysITAdminRole.handle) {
                return true;
            }

            if (userHasITRole) {
                return false;
            }

            if (!userHasSysRole) {
                if (role.handle === sysITAdminRole.handle) {
                    return true;
                }
                if (this.isEditUser) {
                    return this.modalData.userItem.roleHandles[0] === sysAdminRole.handle ? (role.handle === sysAdminRole.handle ? false : true) : (role.handle === sysAdminRole.handle);
                } else {
                    return role.handle === sysAdminRole.handle;
                }
            }
        }
    }
}
